// src/adim/AdminDashboardPage.tsx
import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebaseConfig";
import "../styles/AdminDashboardPage.scss";

interface EventImage {
  url: string;
  caption: string;
}

interface EventEntry {
  id: string;
  event: string;
  category: string;
  images: EventImage[];
  createdAt: { seconds: number };
}

const categories = ["Fashion", "Editorial", "Commercial", "Runway"];

const AdminDashboardPage: React.FC = () => {
  const [events, setEvents] = useState<EventEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<"event" | "category" | "createdAt">("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editEventName, setEditEventName] = useState("");
  const [editCategory, setEditCategory] = useState("");

  const [activeCaptionEvent, setActiveCaptionEvent] = useState<EventEntry | null>(null);
  const [editedCaptions, setEditedCaptions] = useState<string[]>([]);

  useEffect(() => {
    const fetchEvents = async () => {
      const snapshot = await getDocs(collection(db, "events"));
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as EventEntry[];
      setEvents(data);
      setLoading(false);
    };
    fetchEvents();
  }, []);

  const startEdit = (event: EventEntry) => {
    setEditingId(event.id);
    setEditEventName(event.event);
    setEditCategory(event.category);
  };

  const saveEdit = async (id: string) => {
    await updateDoc(doc(db, "events", id), {
      event: editEventName,
      category: editCategory,
    });

    setEvents((prev) =>
      prev.map((e) => (e.id === id ? { ...e, event: editEventName, category: editCategory } : e))
    );
    setEditingId(null);
  };

  const handleDelete = async (id: string) => {
    const confirm = window.confirm("Are you sure you want to delete this event?");
    if (!confirm) return;
    await deleteDoc(doc(db, "events", id));
    setEvents(events.filter((event) => event.id !== id));
  };

  const openCaptionEditor = (event: EventEntry) => {
    setActiveCaptionEvent(event);
    setEditedCaptions(event.images.map((img) => img.caption || ""));
  };

  const saveCaptions = async () => {
    if (!activeCaptionEvent) return;

    const updatedImages = activeCaptionEvent.images.map((img, index) => ({
      url: img.url,
      caption: editedCaptions[index] || "",
    }));

    await updateDoc(doc(db, "events", activeCaptionEvent.id), {
      images: updatedImages,
    });

    setEvents((prev) =>
      prev.map((ev) => (ev.id === activeCaptionEvent.id ? { ...ev, images: updatedImages } : ev))
    );

    setActiveCaptionEvent(null);
    setEditedCaptions([]);
    alert("Captions updated!");
  };

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const filteredAndSorted = events
    .filter((event) =>
      event.event.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aField = sortField === "createdAt"
        ? a.createdAt.seconds
        : (a[sortField] || "").toString().toLowerCase();
      const bField = sortField === "createdAt"
        ? b.createdAt.seconds
        : (b[sortField] || "").toString().toLowerCase();

      if (aField < bField) return sortOrder === "asc" ? -1 : 1;
      if (aField > bField) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

  const handleImageUpload = async (files: FileList) => {
    if (!activeCaptionEvent) return;

    const newImages: EventImage[] = [];
    for (const file of Array.from(files)) {
      const storageRef = ref(storage, `events/${activeCaptionEvent.id}/${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      newImages.push({ url, caption: "" });
    }

    const updatedImages = [...activeCaptionEvent.images, ...newImages];
    setActiveCaptionEvent({ ...activeCaptionEvent, images: updatedImages });
    setEditedCaptions((prev) => [...prev, ...newImages.map(() => "")]);
  };

  const handleImageRemove = (index: number) => {
    if (!activeCaptionEvent) return;
    const updatedImages = [...activeCaptionEvent.images];
    updatedImages.splice(index, 1);

    const updatedCaptions = [...editedCaptions];
    updatedCaptions.splice(index, 1);

    setActiveCaptionEvent({ ...activeCaptionEvent, images: updatedImages });
    setEditedCaptions(updatedCaptions);
  };

  if (loading) return <p>Loading events...</p>;

  return (
    <div className="admin-dashboard">
      <h2>Manage Uploaded Events</h2>

      <input
        type="text"
        placeholder="Search by event name..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="search-input"
      />

      <table className="events-table">
        <thead>
          <tr>
            <th>Preview</th>
            <th onClick={() => handleSort("event")}>Event</th>
            <th onClick={() => handleSort("category")}>Category</th>
            <th>Images</th>
            <th onClick={() => handleSort("createdAt")}>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAndSorted.map((event) => (
            <tr key={event.id}>
              <td>
                {event.images.length > 0 && (
                  <img src={event.images[0].url} alt="preview" className="thumbnail-preview" />
                )}
              </td>
              <td>
                {editingId === event.id ? (
                  <input
                    value={editEventName}
                    onChange={(e) => setEditEventName(e.target.value)}
                  />
                ) : (
                  <span onClick={() => startEdit(event)}>{event.event}</span>
                )}
              </td>
              <td>
                {editingId === event.id ? (
                  <select value={editCategory} onChange={(e) => setEditCategory(e.target.value)}>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                ) : (
                  <span onClick={() => startEdit(event)}>{event.category}</span>
                )}
              </td>
              <td>{event.images.length}</td>
              <td>{new Date(event.createdAt.seconds * 1000).toLocaleDateString()}</td>
              <td>
                {editingId === event.id ? (
                  <>
                    <button onClick={() => saveEdit(event.id)} className="save-btn">✅</button>
                    <button onClick={() => setEditingId(null)} className="cancel-btn">❌</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => openCaptionEditor(event)} className="edit-btn">
                      Edit Captions
                    </button>
                    <button onClick={() => handleDelete(event.id)} className="delete-btn">
                      Delete
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Caption Modal */}
      {activeCaptionEvent && (
        <div className="caption-modal">
          <h3>Edit Captions – {activeCaptionEvent.event}</h3>

          <div className="caption-grid">
            {activeCaptionEvent.images.map((img, index) => (
              <div className="caption-item" key={index}>
                <img src={img.url} alt={`img-${index}`} />
                <input
                  type="text"
                  value={editedCaptions[index]}
                  onChange={(e) => {
                    const newCaptions = [...editedCaptions];
                    newCaptions[index] = e.target.value;
                    setEditedCaptions(newCaptions);
                  }}
                />
                <button onClick={() => handleImageRemove(index)} className="remove-btn">✕</button>
              </div>
            ))}
          </div>

          <div className="upload-more">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => e.target.files && handleImageUpload(e.target.files)}
            />
          </div>

          <div className="caption-buttons">
            <button onClick={saveCaptions}>Save</button>
            <button onClick={() => setActiveCaptionEvent(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;